import { merge, get, isObject, isString } from "lodash";
import { DateTime } from "luxon";
import { COMMUNICATION_TYPES } from "@/constants/communication-types";

export default {
    data() {
        return {
            APPOINTMENT: {
                STATUS_NOT_CONFIRMED: 1,
                STATUS_CONFIRMED: 2,
                STATUS_CANCELLED: 3,
                STATUS_CHECKED_IN: 4,
                STATUS_IN_ROOM: 5,
                STATUS_VISIT_COMPLETE: 6,
                STATUS_CHECKED_OUT: 7,
                STATUS_UNCONFIRMED_OL: 8,
                STATUS_REJECTED: 9,
                STATUS_CHECKIN_UNDONE: 10,
                STATUS_APPT_EDITED: 11,
                STATUS_LEFT_MESSAGE: 12,
                STATUS_PREVISIT: 19
            },
            APPOINTMENT_SOURCE: {
                SOURCE_CLIENT: 0,
                SOURCE_CONTACT: 1,
                SOURCE_COMPANY: 2
            },
            // --based on tbl_status
            get TREATMENT() {
                return {
                    CANCELLED: 13,
                    DISCHARGED: 14,
                    ADMITTED: 15,
                    PRETREATMENT: 16,
                    TREATMENT: 17,
                    POSTTREATMENT: 18
                };
            },
            get TREATMENT_STATUS() {
                return {
                    CANCELLED: -2,
                    DISCHARGED: -1,
                    ADMITTED: 1,
                    PRETREATMENT: 2,
                    TREATMENT: 3,
                    POSTTREATMENT: 4
                };
            },
            get TREATMENT_STATUS_LABELLED() {
                return {
                    [-2]: "Cancelled",
                    [-1]: "Discharged",
                    1: "Admitted",
                    2: "Pre-Treatment",
                    3: "In Treatment",
                    4: "Post-Treatment"
                };
            },
            get TREATMENT_TYPE() {
                return {
                    1: "Medical",
                    2: "Vitals",
                    3: "Other"
                };
            },
            get TREATMENT_TYPE_STR() {
                return {
                    MEDICAL: 1,
                    VITAL: 2,
                    OTHER: 3
                };
            },
            get TREATMENT_STATUS_ORDER() {
                return [1, 2, 3, 4, -1];
            },
            get TREATMENT_ACTIVITY_STATUS() {
                return {
                    PENDING: 0,
                    STARTED: 1,
                    CANCELLED: 2,
                    DEACTIVATED: 3
                };
            },
            get MODULE() {
                return {
                    TREATMENT_BOARD: "TREATMENT_BOARD",
                    PATIENT: "PATIENT"
                };
            }
        };
    },
    methods: {
        /**
         * Generate string for status class based on famfamfam icons.
         * Class should be defined in View not backend.
         * @param{integer} status_id
         * @return{string} CSS class name
         */
        statusClass(status) {
            switch (status) {
                // case this.TREATMENT_ACTIVITY_STATUS.PENDING:
                case this.APPOINTMENT.STATUS_CONFIRMED:
                    return "date";
                case this.APPOINTMENT.STATUS_CANCELLED:
                    return "cancel";
                case this.APPOINTMENT.STATUS_CHECKED_IN:
                    return "accept";
                case this.APPOINTMENT.STATUS_IN_ROOM:
                    return "door";
                // case this.TREATMENT_ACTIVITY_STATUS.STARTED:
                case this.APPOINTMENT.STATUS_VISIT_COMPLETE:
                    return "folder_heart";
                case this.APPOINTMENT.STATUS_CHECKED_OUT:
                    return "door_out";
                case this.APPOINTMENT.STATUS_APPT_EDITED:
                    return "page_white_edit";
                // case this.TREATMENT_ACTIVITY_STATUS.DEACTIVATED:
                case this.APPOINTMENT.STATUS_CHECKIN_UNDONE:
                    return "arrow_undo";
                case this.APPOINTMENT.STATUS_LEFT_MESSAGE:
                    return "arrow_telephone";
                case this.APPOINTMENT.STATUS_PREVISIT:
                    return "page_white_star";
                case this.TREATMENT.DISCHARGED:
                case this.TREATMENT.CANCELLED:
                    return "table_row_delete";
                case this.TREATMENT.ADMITTED:
                    return "table_row_insert";
                case this.TREATMENT.PRETREATMENT:
                    return "table_go";
                case this.TREATMENT.TREATMENT:
                    return "table_lightning";
                case this.TREATMENT.POSTTREATMENT:
                    return "table_refresh";
                default:
                    return "date_error";
            }
        },
        /**
         * Generate string for first time patient or client
         * @param{number} status The first number to add
         * @return{string} String
         */
        _firstTimeText(status, isCreateInPetcare = false) {
            switch (status) {
                case 3:
                    return "New client and patient";
                case 2:
                    return "New client";
                case 1:
                    return isCreateInPetcare
                        ? "New patient - Petcare"
                        : "New patient";
                default:
                    return "";
            }
        },
        _periodText(period) {
            switch (parseInt(period)) {
                case 4:
                    return "Days";
                case 5:
                    return "Weeks";
                case 6:
                    return "Months";
                case 7:
                    return "Years";
                default:
                    return "Days";
            }
        },
        _getStatus() {
            return this.$http
                .get(process.env.SCHEDULE_SERVICE + "status")
                .then(response => response.data.response.resources);
        },
        _getCancellationReasonOptions() {
            return this.$http
                .get(process.env.SCHEDULE_SERVICE + "cancellation-reason")
                .then(response => response.data.response.resources);
        },
        _parseLocation(loc) {
            if (!loc) {
                return null;
            }
            if (isObject(loc)) {
                return `[${loc.type}]${loc.id}`;
            }
            return loc;
        },
        _parseReferrer(referral, subject) {
            if (referral && subject) {
                let type = "contact";

                switch (referral.referral_source) {
                    case 0:
                        type = "client";
                        break;
                    case 1:
                        type = "company";
                        break;
                    case 2:
                        type = "campaign";
                        break;
                    default:
                        break;
                }

                return {
                    id: referral.referral_id,
                    label: subject.label
                        ? subject.label
                        : subject.name
                        ? subject.name + " (" + type + ")"
                        : subject.title + " (" + type + ")",
                    type: referral.referral_source
                };
            }

            return null;
        },
        async _getAppointment(id, params = {}) {
            let data = {};
            const response = await this.$http.get(
                process.env.SCHEDULE_SERVICE + "appointment/" + id,
                { params }
            );
            if (response) {
                data = response.data.response.resources;
                data.start = moment(data.start).tz(this._timeZone);
                data.end = moment(data.end).tz(this._timeZone);
            }
            return data;
        },
        _getDefaultReminderSetting(reminder_setting_type_id = 2) {
            return this.$http
                .get(
                    process.env.ACCOUNT_SERVICE +
                        "reminder-setting?reminder_setting_type_id=" +
                        reminder_setting_type_id
                )
                .then(function (response) {
                    return response.data.response.resources;
                });
        },
        _calculateDue(start, due, type, now) {
            let calculated = {
                text: "",
                time: 0,
                type: type,
                color: "success",
                diff: 0
            };
            if (type === 1) {
                let diff = start.plus({ minutes: due }).diff(now, "minutes");
                calculated.time = start.plus({ minutes: due }).toSeconds();
                calculated.diff = diff.minutes.toFixed();

                if (diff.toObject().minutes <= 5) {
                    calculated.color = "danger";
                }

                if (diff.toObject().minutes <= 0) {
                    calculated.text = "Overdue";
                } else {
                    calculated.text = start
                        .plus({ minutes: due })
                        .toRelative()
                        .replace("in ", "")
                        .trim();
                }
            } else {
                let tzone = this._setting.timezone
                    ? this._setting.timezone
                    : "America/Los_Angeles";
                let specific_time = isString(due)
                    ? DateTime.fromFormat(due + " " + tzone, "hh:mm a z", {
                          setZone: true
                      })
                    : DateTime.fromSeconds(due).setZone(tzone);

                let diff = specific_time.diff(now, "minutes").minutes;
                calculated.time = specific_time.toSeconds();
                calculated.diff = calculated.time;

                if (diff <= 5) {
                    calculated.color = "danger";
                }

                if (diff <= 0) {
                    calculated.text = "Overdue";
                } else if (diff > 1440) {
                    calculated.text = now
                        .plus({ minutes: diff })
                        .toRelative()
                        .replace("in ", "")
                        .trim();
                } else {
                    calculated.text = specific_time.toFormat("hh:mm a");
                }
            }

            return calculated;
        },
        _calculateDueAlt(start, due, type, now) {
            const calculated = {
                text: "",
                time: 0,
                type,
                color: "success",
                diff: 0
            };

            if (type === 1) {
                // TYPE 1 => "X minutes after 'start'"
                const activityTime = start.plus({ minutes: due });
                const diffInMinutes = activityTime.diff(now, "minutes").minutes;
                const activityTimeSeconds = activityTime.toSeconds();

                calculated.time = activityTimeSeconds;
                // If you want diff stored as an integer, floor or round it as needed
                calculated.diff = Math.floor(diffInMinutes);

                if (diffInMinutes <= 5) {
                    calculated.color = "danger";
                }

                if (diffInMinutes <= 0) {
                    calculated.text = "Overdue";
                } else {
                    // If you want a human-readable "X minutes from now" string
                    const relativeStr = activityTime.toRelative() || "";
                    calculated.text = relativeStr.replace("in ", "").trim();
                }
            } else {
                // TYPE != 1 => interpret 'due' as a specific time in a timezone
                const tzone = this._setting.timezone || "America/Los_Angeles";
                const specificTime = isString(due)
                    ? DateTime.fromFormat(due + " " + tzone, "hh:mm a z", {
                          setZone: true
                      })
                    : DateTime.fromSeconds(due).setZone(tzone);

                const diffInMinutes = specificTime.diff(now, "minutes").minutes;
                const specificTimeSeconds = specificTime.toSeconds();

                calculated.time = specificTimeSeconds;
                calculated.diff = Math.floor(diffInMinutes);

                if (diffInMinutes <= 5) {
                    calculated.color = "danger";
                }

                if (diffInMinutes <= 0) {
                    calculated.text = "Overdue";
                } else if (diffInMinutes > 1440) {
                    // More than a day away
                    const relativeStr =
                        now.plus({ minutes: diffInMinutes }).toRelative() || "";
                    calculated.text = relativeStr.replace("in ", "").trim();
                } else {
                    // Within the next 24 hours, just show hh:mm a
                    calculated.text = specificTime.toFormat("hh:mm a");
                }
            }

            return calculated;
        },
        async _saveCheckIn(appointment_data, bundle_data) {
            let self = this;

            self.open_time = moment().tz(self._timeZone);

            let template = null;
            if (appointment_data.admit) {
                template = appointment_data.single_bundle_id;
            } else {
                template =
                    appointment_data.bundle_id.length > 0
                        ? appointment_data.bundle_id
                        : [];
            }

            let post = {
                id: appointment_data.id,
                cid: appointment_data.client_id,
                pid: appointment_data.patient_id,
                entity_id: appointment_data.referrer
                    ? appointment_data.referrer.id
                    : null,
                bid: appointment_data.staff_id,
                note: appointment_data.note,
                soap: appointment_data.soap_id,
                template: template,
                bill_to_invoice: isNaN(appointment_data.bill_to_invoice)
                    ? appointment_data.bill_to_invoice.id
                    : appointment_data.bill_to_invoice,
                date: self.open_time.format(self.getDateFormat()),
                start_time: self.open_time.format("h:mm A"),
                app_date: appointment_data.start.format(self.getDateFormat()),
                location_id: appointment_data.location
                    ? this._parseLocation(appointment_data.location)
                    : null,
                letter_ids: appointment_data.letter_ids
                    ? appointment_data.letter_ids.map(letter => letter)
                    : null,
                reason: appointment_data.reason,
                weight: appointment_data.weight,
                weight_type: appointment_data.uom,
                print_card: appointment_data.print_card,
                print_quantity: appointment_data.print_quantity,
                author_id: appointment_data.author_id,
                triage_id: appointment_data.triage_id,
                triage_type_id: appointment_data.triage_type_id,
                is_admit_treatment: appointment_data.admit
            };

            const webformIds =
                appointment_data.webforms &&
                appointment_data.webforms.length > 0
                    ? appointment_data.webforms.map(webform => webform.id)
                    : [];

            if (webformIds.length > 0) {
                post = Object.assign(post, {
                    checkin_webforms: webformIds,
                    communication_type: appointment_data.communicationType,
                    email:
                        appointment_data.communicationType ===
                        COMMUNICATION_TYPES.EMAIL
                            ? appointment_data.email
                            : null,
                    mobile_country_code:
                        appointment_data.communicationType ===
                        COMMUNICATION_TYPES.SMS
                            ? appointment_data.mobileCountryCode
                            : null,
                    mobile:
                        appointment_data.communicationType ===
                        COMMUNICATION_TYPES.SMS
                            ? appointment_data.mobile
                            : null,
                    is_update_client_contact:
                        appointment_data.isUpdateClientContact
                });
            }

            if (appointment_data.referrer) {
                let type = "actor";

                switch (appointment_data.referrer.type) {
                    case 0:
                        type = "actor";
                        break;
                    case 1:
                        type = "business";
                        break;
                    case 2:
                        type = "campaign";
                        break;
                    default:
                        break;
                }

                post.entity_id =
                    "[" + type + "]" + appointment_data.referrer.id;
            }

            if (bundle_data.selectedElements) {
                bundle_data.selectedElements.forEach(item => {
                    post["fm_bundle_element_" + item.id] = "on";
                    post["min_qty_" + item.id] = item.quantity;
                    if (item.max_quantity) {
                        post["max_qty_" + item.id] = item.max_quantity;
                    }

                    if (item.send_to_lab_value) {
                        post["fm_bundle_element_send_to_lab_" + item.id] = "on";
                    }

                    if (item.tag_number) {
                        post["fm_bundle_element_tag_number_" + item.id] =
                            item.tag_number;
                    }
                });
            }

            const response = await $.post(
                process.env.APPS + "index.php/schedule/checkin/save",
                post
            );
            let admitResponse = null;

            // -- print letter
            if (response && response.state == 0) {
                if (
                    post.letter_ids &&
                    post.letter_ids.length &&
                    response.proceed_letter_print
                ) {
                    post.soap_id = response.soap_id;
                    this._printLetter(post);
                }
                if (post.print_card) {
                    Promise.all([
                        this._printCageCard({
                            client_id: appointment_data.client_id,
                            patient_id: appointment_data.patient_id,
                            printer: appointment_data.print_card,
                            print_quantity: appointment_data.print_quantity
                        })
                    ]);
                }

                if (appointment_data.admit) {
                    admitResponse = await this._saveAdmitTreatment({
                        appointment_id: appointment_data.id,
                        start: 0,
                        admitted_for: appointment_data.reason,
                        patient_id: appointment_data.patient_id,
                        client_id: appointment_data.client_id,
                        staff_id: [appointment_data.staff_id],
                        status_id: 1,
                        template_id: appointment_data.single_bundle_id
                            ? appointment_data.single_bundle_id
                            : null,
                        soap_id: response.soap_id ? response.soap_id : null,
                        invoice_id: !response.isEstimate ? response.id : null,
                        estimate_id: response.isEstimate ? response.id : null,
                        triage_type_id: appointment_data.triage_type_id
                    });
                }
            }
            //! Return respose & admitResponse to use on checkin modal
            return { response, admitResponse };
        },
        async _print_lab_order(params) {
            let self = this;
            let idexx_lab_id = "";
            let idexx_url = "";
            let lab_ids = "";
            let source = "";
            let mod = "";
            let url = "";
            let isEstimate = false;

            if (typeof params.idexx_lab_id !== "undefined") {
                idexx_lab_id = params.idexx_lab_id;
            }

            if (typeof params.idexx_url !== "undefined") {
                idexx_url = params.idexx_url;
            }

            if (typeof params.lab_ids !== "undefined") {
                lab_ids = params.lab_ids;
            }

            if (typeof params.source !== "undefined") {
                source = params.source;
            }

            if (typeof params.module !== "undefined") {
                mod = params.module;
            }

            if (typeof params.url !== "undefined") {
                url = params.url;
            }

            if (typeof params.isEstimate !== "undefined") {
                isEstimate = params.isEstimate;
            }

            if (
                (source.includes("IDEXX") || source.includes("IDEXX_DEVICE")) &&
                (source.includes("ANTECH") ||
                    source.includes("MIDWEST") ||
                    source.includes("NBIOVET") ||
                    source.includes("ZNLABS") ||
                    source.includes("VETEK") ||
                    source.includes("MICROVET") ||
                    source.includes("BIONOTE"))
            ) {
                // --- 1. submit order for IDEXX first
                // --- 2. print requisition form for both IDEXX & Antech
                if (
                    typeof idexx_url !== "undefined" &&
                    idexx_url != "" &&
                    idexx_url != null
                ) {
                    if (!source.includes("IDEXX")) {
                        idexx_lab_id = "";
                    }

                    let i = 0;
                    let loadParams = {
                        idexx_iframe: true,
                        counter: i,
                        idexx_lab_id: idexx_lab_id,
                        idexx_url: idexx_url,
                        lab_ids: lab_ids,
                        module: mod,
                        module_url: url,
                        type: "multiple",
                        isEstimate: isEstimate
                    };
                    return loadParams;
                } else {
                    if (
                        typeof params.idexx_error_msg !== "undefined" &&
                        params.idexx_error_msg != null &&
                        params.idexx_error_msg != ""
                    ) {
                        if (typeof Storage !== "undefined") {
                            self._message(params.idexx_error_msg, "danger");
                            localStorage.setItem(
                                "invoice.dashboard.error_msg",
                                params.idexx_error_msg
                            );
                        }
                    }

                    let print_lab_params = { ids: lab_ids, lab_ids: lab_ids };
                    return isEstimate
                        ? self._print_multiple_requisition_dialog_in_estimate(
                              print_lab_params
                          )
                        : self._print_multiple_requisition_dialog(
                              print_lab_params
                          );
                }
            } else if (
                source.includes("ANTECH") ||
                source.includes("MIDWEST") ||
                source.includes("NBIOVET") ||
                source.includes("ZNLABS") ||
                source.includes("VETEK") ||
                source.includes("MICROVET") ||
                source.includes("BIONOTE")
            ) {
                let print_lab_params = { ids: lab_ids, lab_ids: lab_ids };

                //Need to take into account 'send_to_lab' from labProfile page
                if (params.isLabProfile) {
                    if (params.send_to_lab) {
                        return self._print_multiple_requisition_dialog(
                            print_lab_params
                        );
                    }
                } else if (isEstimate) {
                    return self._print_multiple_requisition_dialog_in_estimate(
                        print_lab_params
                    );
                } else {
                    return self._print_multiple_requisition_dialog(
                        print_lab_params
                    );
                }
            } else if (
                source.includes("IDEXX") ||
                source.includes("IDEXX_DEVICE")
            ) {
                if (!source.includes("IDEXX")) {
                    idexx_lab_id = "";
                }

                if (typeof idexx_url !== "undefined" && idexx_url != "") {
                    let i = 0;
                    let loadParams = {
                        idexx_iframe: true,
                        counter: i,
                        idexx_lab_id: idexx_lab_id,
                        idexx_url: idexx_url,
                        lab_ids: lab_ids,
                        module: mod,
                        module_url: url,
                        type: "single",
                        isEstimate: isEstimate
                    };
                    return loadParams;
                }
            }
        },
        async _print_requisition_dialog(params) {
            let response = await $.get(
                process.env.APPS +
                    "index.php/patient/lab/print_requisition_form_dialog",
                params
            );

            if (response) {
                return response;
            }
        },
        async _print_multiple_requisition_dialog(params) {
            let response = await $.get(
                process.env.APPS +
                    "index.php/patient/lab/print_multiple_requisition_form_dialog",
                params
            );

            if (response) {
                return response;
            }
        },
        async _print_multiple_requisition_dialog_in_estimate(params) {
            let response = await $.get(
                process.env.APPS +
                    "index.php/patient/lab/print_multiple_requisition_form_dialog_in_estimate",
                params
            );

            if (response) {
                return response;
            }
        },
        async _saveStatus(params) {
            let self = this;
            let valid = true;
            self.open_time = moment().tz(self._timeZone);

            try {
                let post = this._createSavePostObject(params, self);
                post = this._modifyPostWithSelectedElements(params, post);
                if (valid) {
                    return await this._saveStatusProceed(params, post, self);
                }
            } catch (error) {
                error.state = 1;
                return error;
            }
        },
        async _saveStatusStripeTerminal(params) {
            this.$root.$on(
                "stripe-reset-terminal",
                this.cancelStripeCollectionSchedule
            );
            this.$root.$on(
                "capturing-stripe-terminal-payment",
                this.capturingStripeTerminalSchedule
            );

            let self = this;
            self.open_time = moment().tz(self._timeZone);

            try {
                params.userId = this.$root.currentActor.id;
                let metadata = this._generateMetadata(params, true);
                this.showPaymentProcessingMessage();

                if (params.isSimulator) {
                    return await self._processStripeTerminal(
                        params,
                        metadata,
                        false // prevent success message
                    );
                } else {
                    let terminalResponse = await self._processServerDrivenStripeTerminal(
                        params,
                        metadata,
                        false
                    );

                    if (terminalResponse === false) {
                        throw Error;
                    }
                }

                this.closePaymentProcessingMessage();

                // NOTE: continue in trigger socket handling.
                // search/see "registerSocketHandling" in (based on which file triggers):
                // - src/module/schedule/appointment/status.vue
                // - src/module/schedule/appointment/check-out/modal.vue
            } catch (error) {
                error.state = 1;
                return error;
            }
        },
        async _printLetterSocket(params) {
            let self = this;

            const letterSave = await $.post(
                process.env.APPS + "index.php/patient/letter/asyncSave",
                params
            );
            if (
                letterSave &&
                letterSave.letter_ids &&
                letterSave.letter_ids.length > 0
            ) {
                letterSave.letter_ids.forEach(async letter => {
                    const processPrint = await $.get(
                        process.env.APPS + "index.php/patient/letter/pdf",
                        {
                            id: letter
                        }
                    );

                    if (processPrint) {
                        if (processPrint.uri) {
                            self._redirect(processPrint.uri, true);
                        }
                    } else {
                        throw {
                            message: "Error while printing letter."
                        };
                    }
                });
            }
        },
        async _printLetter(params) {
            let self = this;
            let letters = params.letter_ids || params.letter;
            let vetId =
                params && params.author_id ? params.author_id : params.bid;

            if (letters && letters.length) {
                return Promise.all(
                    letters.map(async letter => {
                        let data = {
                            pid: params.pid,
                            cid: params.cid,
                            aid: params.id,
                            sid: params.soap_id ? params.soap_id : null,
                            vet_id: vetId,
                            template_id: letter.id,
                            date: params.date,
                            title: letter.name
                        };
                        const letterSave = await $.post(
                            process.env.APPS + "index.php/patient/letter/save",
                            data
                        );

                        if (letterSave && letterSave.id) {
                            const processPrint = await $.get(
                                process.env.APPS +
                                    "index.php/patient/letter/pdf",
                                {
                                    id: letterSave.letter.ID
                                }
                            );
                            if (processPrint) {
                                if (processPrint.uri) {
                                    self._redirect(processPrint.uri, true);
                                }
                            } else {
                                throw {
                                    message: "Error while printing letter."
                                };
                            }
                        } else {
                            throw {
                                message: "Error while saving letter."
                            };
                        }
                    })
                );
            }
        },
        async _printCageCard(params) {
            let self = this;

            if (!params.printer || params.printer == "no") {
                return Promise.resolve(true);
            } else {
                self._message("Printing cage card");
            }

            let print_params = {
                client_id: params.client_id,
                patient_id: params.patient_id,
                printer: params.printer,
                print_quantity: params.print_quantity
            };

            let response = await this.$http.get(
                process.env.APPS +
                    "index.php/patient/cage_card/print_cage_card",
                { params: print_params }
            );

            if (response.data) {
                if (response.data.pdf != null) {
                    self.$root.$emit("bv::hide::modal");
                    self._redirect(response.data.pdf, true, 500);
                } else {
                    if (response.data.cage_card) {
                        try {
                            let cage_card_xml = response.data.cage_card;
                            let label = dymo.label.framework.openLabelXml(
                                cage_card_xml
                            );
                            let printers = dymo.label.framework.getPrinters();

                            //BSTP-3717
                            let print_params = dymo.label.framework.createLabelWriterPrintParamsXml(
                                {
                                    copies: params.print_quantity
                                }
                            );

                            if (printers.length == 0)
                                throw {
                                    message:
                                        "No DYMO printers are installed. Install DYMO printers."
                                };

                            let printerName = "";
                            for (let i = 0; i < printers.length; ++i) {
                                let printer = printers[i];
                                if (
                                    printer.printerType == "LabelWriterPrinter"
                                ) {
                                    printerName = printer.name;
                                    break;
                                }
                            }

                            if (printerName == "") {
                                throw {
                                    message:
                                        "No LabelWriter printers found. Install LabelWriter printer"
                                };
                            }

                            // finally print the label
                            label.print(printerName, print_params);

                            if (response.data.message) {
                                self._message(response.data.message, "success");
                            }
                        } catch (e) {
                            self._message(e.message, "danger");
                        }
                    } else {
                        if (response.data.state && response.data.state == 0) {
                            if (response.data.message) {
                                self._message(response.data.message, "success");
                            }
                        } else {
                            if (response.data.message) {
                                self._message(response.data.message, "success");
                            }
                        }
                    }
                }
            }

            return response.data;
        },
        async _printLabel(params) {
            let self = this;

            if (!params.printer || params.printer == "no") {
                return Promise.resolve(true);
            } else {
                self._message("Printing label");
            }

            let print_params = {
                record_id: params.record_id,
                module_type: params.module_type,
                printer: params.printer,
                print_quantity: params.print_quantity
            };

            let response = await this.$http.get(
                process.env.APPS +
                    "index.php/patient/prescription/print_label_submit",
                { params: print_params }
            );

            if (response.data) {
                if (response.data.pdf) {
                    self.$root.$emit("bv::hide::modal");
                    self._redirect(response.data.pdf, true, 500);
                } else {
                    if (response.data.labelXML) {
                        try {
                            let labelXml = "";
                            if (response.data.labelXMLlogo != "") {
                                labelXml = response.data.labelXML.labelXml1.concat(
                                    response.data.labelXML.labelXml2,
                                    response.data.labelXML.labelXml3
                                );
                            } else {
                                labelXml = response.data.labelXML.labelXml1.concat(
                                    response.data.labelXML.labelXml3
                                );
                            }

                            let label = dymo.label.framework.openLabelXml(
                                labelXml
                            );
                            let printers = dymo.label.framework.getPrinters();

                            let print_params = dymo.label.framework.createLabelWriterPrintParamsXml(
                                {
                                    copies: params.print_quantity
                                }
                            );

                            if (printers.length == 0)
                                throw {
                                    message:
                                        "No DYMO printers are installed. Install DYMO printers."
                                };

                            let printerName = "";
                            for (let i = 0; i < printers.length; ++i) {
                                let printer = printers[i];
                                if (
                                    printer.printerType == "LabelWriterPrinter"
                                ) {
                                    printerName = printer.name;
                                    break;
                                }
                            }

                            if (printerName == "") {
                                throw {
                                    message:
                                        "No LabelWriter printers found. Install LabelWriter printer"
                                };
                            }

                            // finally print the label
                            label.print(printerName, print_params);

                            if (response.data.message) {
                                self._message(response.data.message, "success");
                            }
                        } catch (e) {
                            self._message(e.message, "danger");
                        }
                    } else {
                        if (response.data.state && response.data.state == 0) {
                            if (response.data.message) {
                                self._message(response.data.message, "success");
                            }
                        } else {
                            if (response.data.message) {
                                self._message(response.data.message, "warning");
                            }
                        }
                    }
                }
            }

            return response.data;
        },
        async _medicalNote(data) {
            let self = this;

            let post = {
                id: data.id,
                isShortcut: data.isShortcut,
                isShortcut_pid: data.patient ? data.patient.id : null,
                patient: data.patient ? data.patient.fullname : null,
                cid: data.client ? data.client.id : null,
                entity_id: data.referral_id ? data.referral_id : null,
                entity_type: data.referrer ? data.referrer.type : null,
                entity_name: data.referrer ? data.referrer.label : null,
                start: data.date.format(self.getDateFormat()),
                note_date: data.note_date.format(self.getDateFormat()),
                note_time: data.note_date.format("h:mm A"),
                template: data.soap_id,
                complaint: data.complaint,
                bid: data.staffs ? data.staffs.actor_id : null,
                by: data.staffs ? data.staffs.fullname : null
            };

            const response = await $.post(
                process.env.APPS + "index.php/patient/soap/save",
                post
            );

            // -- print letter
            if (response && response.status == 0) {
                return response;
            }

            return response;
        },
        async _saveAdmitTreatment(params) {
            let response = false;

            params = merge(params, {
                room_id: null,
                status_id: 1
            });

            try {
                this.loading = true;
                response = await this.$http.post(
                    process.env.BARRACUDA + "schedule/treatment-board",
                    params
                );

                if (response.status === 200) {
                    this.loading = false;

                    if (
                        response &&
                        response.data.response.message.code === 200
                    ) {
                        this.$socket.client.emit("v-broadcast", {
                            params: response.data.response,
                            instanceId: this.instanceId,
                            action: "refresh-treatment",
                            businessId: this._setting.business_id
                        });
                    }
                }
            } catch (error) {
                this.loading = false;
                this._message("Error saving data", "danger");
                console.error(error);
            }

            return response;
        },
        cancelStripeCollectionSchedule(terminal) {
            this.$root.$emit("stripe-reset-terminal-schedule", terminal);
        },
        capturingStripeTerminalSchedule() {
            this.$root.$emit("capturing-stripe-terminal-payment-schedule");
        },
        _createSavePostObject(params, self) {
            let template = null;
            if (params.admit) {
                template = params.single_bundle_id;
            } else {
                if (params.bundle_id) {
                    template =
                        params.bundle_id.length > 0 ? params.bundle_id : [];
                }
            }

            let post = {
                fm_census_appointment_id: params.id,
                fm_census_status_id: params.status_id,
                fm_census_reason_id: params.reason_id ? params.reason_id : null,
                fm_census_room_id: params.room_id ? params.room_id : null,
                fm_census_note: params.note,
                fm_census_confirmed_date: params.confirmed_date
                    ? params.confirmed_date.format(self.getDateFormat())
                    : null,
                fm_census_confirmed_time: params.confirmed_time
                    ? params.confirmed_time.format("h:mm A")
                    : null,
                fm_census_client_id: params.client_id,
                fm_census_patient_id: params.patient_id,
                fm_census_entity_id: params.entity_id,
                fm_census_provider_id: params.provider_id,
                fm_census_soap_id: params.soap_id,
                fm_census_bundle_id: template,
                fm_census_invoice_id: params.bill_to_invoice
                    ? isNaN(params.bill_to_invoice)
                        ? params.bill_to_invoice.id
                        : params.bill_to_invoice
                    : null,
                fm_census_check_in_date: params.open_time.format(
                    self.getDateFormat()
                ),
                fm_census_check_in_time: self.open_time.format("h:mm A"),
                checkout_date: params.checkout_date
                    ? params.checkout_date.format(self.getDateFormat())
                    : null,
                checkout_time: params.checkout_time
                    ? params.checkout_time.format("h:mm A")
                    : null,
                fm_census_appointment_date: params.start
                    ? params.start.format(self.getDateFormat())
                    : null,
                fm_census_location: params.location
                    ? this._parseLocation(params.location)
                    : null,
                fm_census_reason: params.reason ? params.reason : null,
                fm_census_weight: params.weight ? params.weight : null,
                fm_census_weight_type: params.uom ? params.uom : null,
                fm_census_print_card_option: params.print_card
                    ? params.print_card
                    : null,
                fm_census_print_quantity: params.print_quantity
                    ? params.print_quantity
                    : null,
                fm_census_invoices: params.invoices ? params.invoices : null,
                fm_census_credits: params.credits ? params.credits : null,
                fm_census_invoice_amount: params.invoice_amount
                    ? params.invoice_amount
                    : null,
                fm_census_credit_amount: params.credit_amount
                    ? params.credit_amount
                    : null,
                fm_census_payment_amount: params.payment_amount
                    ? params.payment_amount
                    : null,
                fm_census_payment_type: params.payment_type
                    ? params.payment_type
                    : null,
                fm_census_change: params.change ? params.change : null,
                fm_census_documents: params.documents ? params.documents : null,
                fm_census_export_type: params.export_type
                    ? params.export_type
                    : null,
                fm_census_export_message: params.export_message
                    ? params.export_message
                    : null,
                fm_census_anet_transaction_id: params.anet_transaction_id
                    ? params.anet_transaction_id
                    : null,
                fm_census_anet_transaction_name: params.anet_transaction_name
                    ? params.anet_transaction_name
                    : null,
                fm_census_anet_transaction_output: params.anet_transaction_output
                    ? params.anet_transaction_output
                    : null,
                fm_census_anet_transaction_track: params.anet_transaction_track
                    ? params.anet_transaction_track
                    : null,
                payment_description: params.payment_description
                    ? params.payment_description
                    : null, // -- cardconnect terminal
                signature_cc: params.signature_cc ? params.signature_cc : null, // -- cardconnect terminal
                fm_census_payment_id: params.payment_id
                    ? params.payment_id
                    : null, // cardconnect key-in & card on file
                fm_census_record_id: get(params, "record_id"),
                fm_census_tag: get(params, "tag"),
                fm_census_from_census: params.from_census
                    ? params.from_census
                    : null,
                terminal: params.terminal ? params.terminal : null, // -- cardconnect terminal
                swipe_unique: params.swipe_unique ? params.swipe_unique : null, // -- cardconnect terminal
                unique_id: params.unique_id ? params.unique_id : null,
                fm_census_letter_ids: params.letter_ids
                    ? params.letter_ids.map(letter => letter.id)
                    : null,
                letters: params.letter_ids
                    ? params.letter_ids.map(letter => letter)
                    : null,
                fm_census_external_id: params.external_id
                    ? params.external_id
                    : null,
                letter_ids: params.letter_ids
                    ? params.letter_ids.map(letter => letter.id)
                    : [],
                fund_id: params.fund_id ? params.fund_id : null,
                pay_from_fund: params.pay_from_fund ? params.pay_from_fund : 0,
                change_to_fund: params.change_to_fund
                    ? params.change_to_fund
                    : 0,
                triage_type_id: params.triage_type_id
                    ? params.triage_type_id
                    : null,
                include_declined: params.includeDeclined
            };

            if (params.status_id == self.APPOINTMENT.STATUS_CHECKED_IN) {
                post.is_admit_treatment = params.admit; // VET-8062
            }

            const webformIds =
                params.webforms && params.webforms.length > 0
                    ? params.webforms.map(webform => webform.id)
                    : [];

            if (webformIds.length > 0) {
                post = Object.assign(post, {
                    checkin_webforms: webformIds.length > 0 ? webformIds : [],
                    communication_type: params.communicationType,
                    email:
                        params.communicationType === COMMUNICATION_TYPES.EMAIL
                            ? params.email
                            : null,
                    mobile_country_code:
                        params.communicationType === COMMUNICATION_TYPES.SMS
                            ? params.mobileCountryCode
                            : null,
                    mobile:
                        params.communicationType === COMMUNICATION_TYPES.SMS
                            ? params.mobile
                            : null,
                    is_update_client_contact: params.isUpdateClientContact
                });
            }

            return post;
        },
        async _saveStatusProceed(params, post, self) {
            const response = await this.$http.post(
                `${process.env.APPS}index.php/schedule/census/saveStatus`,
                $.param(post)
            );

            let admitResponse = null;

            if (get(response, "data.state") === 0) {
                if (
                    params.letter_ids &&
                    params.letter_ids.length &&
                    get(response, "data.proceed_letter_print")
                ) {
                    let letter_soap_id = get(response, "data.soap_id");
                    await this._printLetter({
                        pid: params.patient_id,
                        cid: params.client_id,
                        id: params.id,
                        soap_id: letter_soap_id ? letter_soap_id : null,
                        bid: params.staff_id,
                        letter: params.letter_ids ? params.letter_ids : [],
                        date: (params.date || params.open_time).format(
                            self.getDateFormat()
                        )
                    });
                }
                if (get(response, "data.cage_card")) {
                    await this._printCageCard({
                        client_id: params.client_id,
                        patient_id: params.patient_id,
                        printer: params.print_card,
                        print_quantity: params.print_quantity
                    });
                }
                if (params.admit) {
                    let bundleTemplate = self.appointment_data.bundle_id;
                    let templateId = null;

                    if (bundleTemplate) {
                        if (Array.isArray(templateId)) {
                            if (templateId.length > 0) {
                                templateId = templateId[0]["id"];
                            } else {
                                templateId = null;
                            }
                        }
                    } else if (
                        !bundleTemplate &&
                        self.appointment_data.single_bundle_id
                    ) {
                        templateId = self.appointment_data.single_bundle_id;
                    } else {
                        templateId = null;
                    }

                    admitResponse = await this._saveAdmitTreatment({
                        appointment_id: params.id,
                        start: 0,
                        admitted_for: params.reason,
                        patient_id: params.patient_id,
                        client_id: params.client_id,
                        staff_id: [params.staff_id],
                        status_id: 1,
                        template_id: templateId ? templateId : null,
                        soap_id: get(response, "data.soap_id")
                            ? get(response, "data.soap_id")
                            : null,
                        invoice_id: !get(response, "data.isEstimate")
                            ? get(response, "data.id")
                            : null,
                        estimate_id: get(response, "data.isEstimate")
                            ? get(response, "data.id")
                            : null,
                        triage_type_id: params.triage_type_id
                    });
                }
                //! Return respose & admitResponse to use on status modal
                return { response: response.data, admitResponse };
            } else {
                return { response: response.data };
            }
        },
        _modifyPostWithSelectedElements(params, post) {
            if (params.selectedElements && params.selectedElements.length > 0) {
                params.selectedElements.forEach(item => {
                    post["fm_bundle_element_" + item.id] = "on";
                    post["min_qty_" + item.id] = item.quantity;

                    if (item.max_quantity) {
                        post["max_qty_" + item.id] = item.max_quantity;
                    }

                    if (item.send_to_lab_value) {
                        post["fm_bundle_element_send_to_lab_" + item.id] = "on";
                    }

                    if (item.tag_number) {
                        post["fm_bundle_element_tag_number_" + item.id] =
                            item.tag_number;
                    }
                });
            }
            return post;
        }
    }
};
